import React, { useState } from "react";
import { format, addDays, parseISO } from "date-fns";
import { FiCalendar, FiInfo, FiHome } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const PregnancyDueDateEstimator = () => {
  const [calculationMethod, setCalculationMethod] = useState("lmp");
  const [lastPeriodDate, setLastPeriodDate] = useState("");
  const [conceptionDate, setConceptionDate] = useState("");
  const [results, setResults] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  const calculateDueDate = (e) => {
    e.preventDefault();

    let dueDate;
    if (calculationMethod === "lmp" && lastPeriodDate) {
      dueDate = addDays(parseISO(lastPeriodDate), 280);
    } else if (calculationMethod === "conception" && conceptionDate) {
      dueDate = addDays(parseISO(conceptionDate), 266);
    } else {
      return; // No valid input
    }

    setResults({
      dueDate,
      calculationMethod,
      trimesterDates: {
        first: addDays(dueDate, -280),
        second: addDays(dueDate, -196), // 12 weeks
        third: addDays(dueDate, -84), // 28 weeks
      },
    });
  };

  const formatDateLong = (date) => {
    return format(date, "EEEE, do MMMM, yyyy");
  };

  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8">
   <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Pregnancy Due Date Calculator - FlowHearts | Track Trimesters & Delivery</title>
    <meta
      name="description"
      content="Calculate your pregnancy due date and trimester timeline with FlowHearts' Due Date Calculator. Plan your pregnancy journey with accurate delivery estimates."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/due-date-estimator" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/due-date-estimator" />
    <meta
      property="og:title"
      content="Pregnancy Due Date Calculator - FlowHearts | Track Trimesters & Delivery"
    />
    <meta
      property="og:description"
      content="Use FlowHearts' Due Date Calculator to estimate your pregnancy due date, track trimesters, and prepare for your baby's arrival with confidence."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/due-date-estimator-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/due-date-estimator" />
    <meta
      property="twitter:title"
      content="Pregnancy Due Date Calculator - FlowHearts | Track Trimesters & Delivery"
    />
    <meta
      property="twitter:description"
      content="Estimate your pregnancy due date and trimester timeline with FlowHearts' Due Date Calculator for informed pregnancy planning and maternal health."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/due-date-estimator-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Pregnancy Due Date Calculator",
          "url": "https://flowhearts.com/due-date-estimator",
          "description": "Calculate your pregnancy due date and track trimesters with FlowHearts' Due Date Calculator for a confident pregnancy journey.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        <form onSubmit={calculateDueDate}>
          <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl p-8 shadow-xl">
            <div className="flex justify-between items-start">
              <h2 className="text-4xl font-cormorant font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
                Pregnancy Due Date Estimator
              </h2>
              <button
                type="button"
                onClick={() => setShowInfo(!showInfo)}
                className="text-purple-600 hover:text-pink-500"
              >
                <FiInfo size={24} />
              </button>
            </div>

            {showInfo && (
              <div className="mb-8 bg-blue-50 rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-purple-700">
                  About Due Date Estimation
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    • Based on last menstrual period (LMP) or conception date
                  </li>
                  <li>
                    • Only ~5% of babies are born exactly on their due date
                  </li>
                  <li>• Most births occur between 37-42 weeks</li>
                  <li>
                    • Helps track pregnancy milestones and fetal development
                  </li>
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div
                className={`p-4 rounded-xl cursor-pointer transition-all ${
                  calculationMethod === "lmp"
                    ? "bg-purple-100 border-2 border-purple-300"
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
                onClick={() => setCalculationMethod("lmp")}
              >
                <h3 className="font-bold text-lg mb-2 text-purple-700">
                  Last Menstrual Period Method
                </h3>
                <p className="text-gray-600 mb-3">
                  Adds 280 days (40 weeks) to your LMP start date
                </p>
                {calculationMethod === "lmp" && (
                  <div className="mt-4">
                    <label className="block text-md font-medium mb-2 text-purple-700">
                      <FiCalendar className="inline mr-2" />
                      First day of last period
                    </label>
                    <input
                      type="date"
                      value={lastPeriodDate}
                      onChange={(e) => setLastPeriodDate(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                      required
                      max={format(new Date(), "yyyy-MM-dd")}
                    />
                  </div>
                )}
              </div>

              <div
                className={`p-4 rounded-xl cursor-pointer transition-all ${
                  calculationMethod === "conception"
                    ? "bg-purple-100 border-2 border-purple-300"
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
                onClick={() => setCalculationMethod("conception")}
              >
                <h3 className="font-bold text-lg mb-2 text-purple-700">
                  Conception Date Method
                </h3>
                <p className="text-gray-600 mb-3">
                  Adds 266 days (38 weeks) to conception date (click here to
                  check..)
                </p>
                {calculationMethod === "conception" && (
                  <div className="mt-4">
                    <label className="block text-md font-medium mb-2 text-purple-700">
                      <FiCalendar className="inline mr-2" />
                      Estimated conception date
                    </label>
                    <input
                      type="date"
                      value={conceptionDate}
                      onChange={(e) => setConceptionDate(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                      required
                      max={format(new Date(), "yyyy-MM-dd")}
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
            >
              Calculate Due Date
            </button>

            {results && (
              <div className="mt-12 space-y-8">
                <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-semibold mb-6 text-center text-purple-700">
                    Your Pregnancy Timeline
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-5 rounded-xl shadow-sm text-center">
                      <h4 className="font-bold text-lg mb-3 text-pink-600">
                        First Trimester
                      </h4>
                      <p className="text-gray-700 mb-2">Weeks 1-12</p>
                      <p className="text-sm text-gray-500">
                        {format(results.trimesterDates.first, "MMM d, yyyy")} to
                        <br />
                        {format(
                          addDays(results.trimesterDates.second, -1),
                          "MMM d, yyyy"
                        )}
                      </p>
                    </div>

                    <div className="bg-white p-5 rounded-xl shadow-sm text-center border-2 border-purple-300">
                      <h4 className="font-bold text-lg mb-3 text-purple-600">
                        Estimated Due Date
                      </h4>
                      <p className="text-2xl font-bold text-purple-700 mb-2">
                        {formatDateLong(results.dueDate)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {results.calculationMethod === "lmp"
                          ? "Calculated from last menstrual period"
                          : "Calculated from conception date"}
                      </p>
                    </div>

                    <div className="bg-white p-5 rounded-xl shadow-sm text-center">
                      <h4 className="font-bold text-lg mb-3 text-blue-600">
                        Third Trimester
                      </h4>
                      <p className="text-gray-700 mb-2">Weeks 28-40</p>
                      <p className="text-sm text-gray-500">
                        {format(results.trimesterDates.third, "MMM d, yyyy")} to
                        <br />
                        {format(results.dueDate, "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-xl">
                  <p className="text-yellow-700">
                    <span className="font-medium">ℹ️ Important Note:</span> Only
                    about 5% of babies are born on their exact due date. Most
                    births occur between 37-42 weeks. This estimate helps track
                    pregnancy progress but isn't an exact prediction.
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-6 mt-12">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* How to Use Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-pink-500" />
              How to Use the Due Date Estimator
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Select Calculation Method
                </h3>
                <p className="text-gray-700 text-center">
                  Choose between Last Menstrual Period (LMP) or Conception Date
                  calculation
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Enter Your Date
                </h3>
                <p className="text-gray-700 text-center">
                  Input either your last period start date or known conception
                  date
                </p>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Get Results
                </h3>
                <p className="text-gray-700 text-center">
                  View your estimated due date and trimester breakdown
                </p>
              </div>
            </div>
          </div>

          {/* The Science Behind */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiInfo className="mr-3 text-purple-500" />
              The Science Behind Due Date Calculation
            </h2>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Calculation Methods
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">LMP Method</p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Due Date = LMP + 280 days
                  </code>
                  <p className="text-sm text-gray-600 mt-2">
                    Assumes conception occurred ~14 days after LMP
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Conception Method
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Due Date = Conception + 266 days
                  </code>
                  <p className="text-sm text-gray-600 mt-2">
                    Based on 38 weeks gestation from conception
                  </p>
                </div>
              </div>

              <p className="text-gray-700">
                These calculations follow the{" "}
                <span className="font-semibold">Naegele's Rule</span>, a
                standard obstetrical formula developed in the 19th century. The
                280-day pregnancy duration accounts for an average 266-day
                gestation plus 14 days from LMP to conception.
              </p>
              <p className="text-gray-700 mt-4">
                This approach remains a cornerstone in modern maternity care, helping expectant mothers plan their pregnancy journey with greater confidence.
              </p>
            </div>
          </div>

          {/* Biological Basis */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-green-500" />
              Pregnancy Timeline Biology
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-green-700 mb-3">
                  First Trimester
                </h3>
                <p className="text-gray-700 mb-2">Weeks 1-12</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Major organ systems form</li>
                  <li>• Embryo becomes fetus</li>
                  <li>• Highest miscarriage risk</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">
                  Second Trimester
                </h3>
                <p className="text-gray-700 mb-2">Weeks 13-27</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Fetal movement begins</li>
                  <li>• Organs mature</li>
                  <li>• Gender visible on ultrasound</li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-3">
                  Third Trimester
                </h3>
                <p className="text-gray-700 mb-2">Weeks 28-40</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Rapid growth and fat deposition</li>
                  <li>• Lung maturation</li>
                  <li>• Fetal positioning for birth</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Accuracy Note */}
          <div className="bg-white rounded-xl p-6 shadow-inner border border-pink-300 mb-12">
            <h3 className="text-xl font-semibold text-pink-800 mb-4 flex items-center">
              <FiInfo className="mr-2 text-pink-500" />
              Important Note on Accuracy
            </h3>
            <p className="text-gray-700 mb-3">
              Due date estimates are based on averages and have several
              limitations:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <FiCalendar className="text-pink-500 mr-2 mt-0.5" />
                <span>Only 5% of babies are born on their exact due date</span>
              </li>
              <li className="flex items-start">
                <FiCalendar className="text-pink-500 mr-2 mt-0.5" />
                <span>Normal pregnancies can vary by ±2 weeks</span>
              </li>
              <li className="flex items-start">
                <FiCalendar className="text-pink-500 mr-2 mt-0.5" />
                <span>
                  Ultrasound measurements become more accurate than LMP dates
                  after first trimester
                </span>
              </li>
            </ul>
            <p className="text-gray-700">
              Always consult with your healthcare provider for personalized
              pregnancy dating and monitoring. Regular check-ups ensure optimal maternal and fetal health throughout the journey.
            </p>
          </div>

          {/* New Section: Why Estimate Your Due Date? */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-pink-500" />
              Why Estimate Your Due Date?
            </h2>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <p className="text-gray-700 mb-4">
                Knowing your estimated due date is more than just marking a calendar—it's the foundation of your pregnancy experience. In today's focus on empowered maternity, understanding this timeline supports better preparation and peace of mind.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FiInfo className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Medical Monitoring:</strong> Guides prenatal appointments, screenings, and ultrasounds to track baby development milestones effectively.</span>
                </li>
                <li className="flex items-start">
                  <FiInfo className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Practical Planning:</strong> Helps arrange maternity leave, nursery setup, and family support, aligning with modern expectant parent lifestyles.</span>
                </li>
                <li className="flex items-start">
                  <FiInfo className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Emotional Preparation:</strong> Builds anticipation through weekly progress tracking, fostering a positive pregnancy mindset.</span>
                </li>
                <li className="flex items-start">
                  <FiInfo className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Health Awareness:</strong> Enables timely recognition of potential preterm or post-term risks, promoting proactive maternal wellness.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                With tools like this due date estimator, you're equipping yourself for a more informed and enjoyable path to parenthood.
              </p>
            </div>
          </div>

          {/* New Section: Frequently Asked Questions */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiInfo className="mr-3 text-purple-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  How accurate is the due date calculation?
                </h3>
                <p className="text-gray-700">
                  While based on established medical formulas, actual delivery can vary. Most babies arrive within two weeks of the estimate.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  What's the difference between LMP and conception methods?
                </h3>
                <p className="text-gray-700">
                  LMP adds 280 days assuming standard cycles, while conception adds 266 days from the actual fertilization date.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Can ultrasounds change the due date?
                </h3>
                <p className="text-gray-700">
                  Yes, early ultrasounds provide precise measurements and may adjust the estimate for better accuracy.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  What if my cycles are irregular?
                </h3>
                <p className="text-gray-700">
                  The conception method or ultrasound dating might be more reliable. Discuss with your doctor for personalized assessment.
                </p>
              </div>
            </div>
          </div>

          {/* New Section: Tips for Your Pregnancy Journey */}
          <div>
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-pink-500" />
              Tips for Your Pregnancy Journey
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Nutrition Focus
                </h3>
                <p className="text-gray-700">
                  Prioritize balanced meals with folic acid, iron, and calcium to support healthy fetal growth and maternal energy.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Exercise Routine
                </h3>
                <p className="text-gray-700">
                  Incorporate gentle activities like prenatal yoga or walking to maintain fitness and reduce common discomforts.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Emotional Wellness
                </h3>
                <p className="text-gray-700">
                  Practice mindfulness or join support groups to navigate hormonal changes and build a positive mindset.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Prenatal Care
                </h3>
                <p className="text-gray-700">
                  Schedule regular check-ups to monitor progress and address any concerns early in your journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PregnancyDueDateEstimator;